/**
 * Wraps an async function with automatic 401 retry logic.
 *
 * When the wrapped function throws an error containing '401' or 'unauthorized'
 * in its message, this helper will attempt a token refresh and, if successful,
 * retry the original function once.
 *
 * Two overloads are provided:
 *
 * - Standard variant (with refreshToken guard): only retries when `refreshToken`
 *   is truthy. Used by the majority of hooks.
 *
 * - Unchecked variant (without refreshToken guard): retries whenever the error
 *   message contains '401' or 'unauthorized'. Used by hooks that do not guard
 *   on the presence of a refresh token before retrying (e.g. useHsmBatches,
 *   useHsmCampaigns).
 */

export async function withAuthRetry<T>(
  fn: () => Promise<T>,
  refreshToken: string | undefined,
  attemptRefresh: () => Promise<boolean>
): Promise<T>;

export async function withAuthRetry<T>(
  fn: () => Promise<T>,
  attemptRefresh: () => Promise<boolean>
): Promise<T>;

export async function withAuthRetry<T>(
  fn: () => Promise<T>,
  refreshTokenOrAttemptRefresh: string | undefined | (() => Promise<boolean>),
  maybeAttemptRefresh?: () => Promise<boolean>
): Promise<T> {
  // Determine which overload was called
  const isUnchecked = typeof refreshTokenOrAttemptRefresh === 'function';
  const refreshToken = isUnchecked ? undefined : refreshTokenOrAttemptRefresh;
  const attemptRefresh = isUnchecked
    ? (refreshTokenOrAttemptRefresh as () => Promise<boolean>)
    : (maybeAttemptRefresh as () => Promise<boolean>);

  try {
    return await fn();
  } catch (err) {
    const message = err instanceof Error ? err.message : '';
    const is401 = message.includes('401') || message.includes('unauthorized');

    // Standard variant: only retry when refreshToken is present
    // Unchecked variant: retry whenever is401 is true
    const shouldRetry = is401 && (isUnchecked ? true : Boolean(refreshToken));

    if (shouldRetry) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return await fn();
      }
    }

    throw err;
  }
}
